import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useForgetPasswordMutation } from '../../../queries/auth';
import AuthShell from '../../../components/auth/AuthShell';
import { ErrorBanner, Field, Head, Submit, focusFirstInvalid, withNode } from '../../../components/auth/parts';
import { classify } from '../../../components/auth/authErrors';
import { emailOk } from '../../../components/auth/validators';
import useCountdown from '../../../components/auth/useCountdown';
import useAuthTitle from '../../../components/auth/useAuthTitle';

const RESEND_SECONDS = 60;

function ForgetPassword () {
  const { t } = useTranslation();
  useAuthTitle('forgot');
  const a = (k, o) => t(`authPage.${k}`, o);

  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [sentTo, setSentTo] = useState(null);
  const [banner, setBanner] = useState(null); // rate | offline | server
  const formRef = useRef(null);
  const resend = useCountdown(); // gates the "resend" button
  const rate = useCountdown();   // gates the form after a 429
  const forget = useForgetPasswordMutation({ inline: true });
  const rateLocked = banner === 'rate' && rate.left > 0;

  const send = async (address) => {
    setBanner(null);
    try {
      await forget.mutateAsync({ email: address });
    } catch (e) {
      const info = classify(e);
      // Anti-enumeration: an "unknown email" answer (422 from a backend that
      // still validates existence) looks exactly like success.
      if (info.kind !== 'validation') {
        if (info.kind === 'rate') { rate.start(info.retryAfter); setBanner('rate'); }
        else if (info.kind === 'offline') setBanner('offline');
        else setBanner('server');
        return false;
      }
    }
    setSentTo(address);
    resend.start(RESEND_SECONDS);
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (rateLocked || forget.isLoading) return;
    let m = '';
    if (!email.trim()) m = a('eReq'); else if (!emailOk(email)) m = a('eEmail');
    setErr(m);
    if (m) { focusFirstInvalid(formRef.current); return; }
    await send(email.trim());
  };

  if (sentTo) {
    const S = (k, o) => t(`authPage.forgot.${k}`, o);
    const waiting = resend.left > 0 || rate.left > 0;
    return (
      <AuthShell>
        <Head
          icon="mail" title={S('sentH')}
          text={withNode(S('sentP', { m: '{{m}}' }), <span className="ppa-mail">{sentTo}</span>)}
        />
        {banner && <ErrorBanner kind={banner} left={rate.left} />}
        <p className="ppa-hint flush">{S('sentHint')}</p>
        <button
          className="ppa-btn outline" type="button" id="resend"
          disabled={waiting || forget.isLoading}
          aria-busy={forget.isLoading ? 'true' : undefined}
          onClick={() => send(sentTo)}
        >
          {resend.left > 0
            ? withNode(S('resend', { s: '{{m}}' }), <span id="rs">{resend.left}</span>)
            : S('resendNow')}
        </button>
        <p className="ppa-alt"><Link to="/login">{S('back')}</Link></p>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Head icon="key" title={a('forgot.h')} text={a('forgot.p')} />
      <form ref={formRef} noValidate onSubmit={onSubmit}>
        {banner && <ErrorBanner kind={banner} left={rate.left} />}
        <Field
          id="email" label={a('email')} type="email" ltr autoComplete="email" placeholder="name@cafe.sa" 
          value={email} error={err}
          onChange={(e) => { setEmail(e.target.value); setErr(''); }}
        />
        <Submit busy={forget.isLoading} busyLabel={a('forgot.busy')} disabled={rateLocked}>{a('forgot.btn')}</Submit>
      </form>
      <p className="ppa-alt"><Link to="/login">{a('forgot.back')}</Link></p>
    </AuthShell>
  );
}

export default ForgetPassword;
